'use client';

import { ShieldCheckIcon, LockClosedIcon, DocumentTextIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface DPDPPrivacyDisclosureProps {
  currentLanguage: string;
}

const DPDPPrivacyDisclosure = ({ currentLanguage }: DPDPPrivacyDisclosureProps) => {
  const content = {
    en: {
      title: 'DPDP Privacy & Data Protection',
      subtitle: 'Digital Personal Data Protection Act 2023 Compliance',
      sections: [
        {
          icon: ShieldCheckIcon,
          title: 'Data Collection & Purpose',
          items: [
            'User login credentials (encrypted)',
            'IP addresses for security monitoring',
            'Geographic location data (city/region level)',
            'Session metadata and timestamps',
            'Browser and device information',
          ],
          purpose: 'Collected for: Security, service improvement, regulatory compliance, fraud prevention',
        },
        {
          icon: LockClosedIcon,
          title: 'Data Security & Storage',
          items: [
            'SSL/TLS encryption for all data transmission',
            'AES-256 encryption for stored data',
            'Secure servers located in India',
            'Regular security audits and compliance checks',
            'Data retention: 7 years (as per DPDP Act)',
          ],
          purpose: 'Your data is protected with bank-grade security standards',
        },
        {
          icon: DocumentTextIcon,
          title: 'Your Rights Under DPDP Act',
          items: [
            'Right to access your personal data',
            'Right to correction of inaccurate data',
            'Right to erasure/deletion of data',
            'Right to data portability',
            'Right to withdraw consent',
          ],
          purpose: 'Exercise your rights: dr.nupur@gencarbon.in',
        },
        {
          icon: GlobeAltIcon,
          title: 'International Data Transfers',
          items: [
            'Primary data storage: India',
            'Backup servers: ISO 27001 certified facilities',
            'Cross-border transfers: Only to approved countries',
            'Third-party processors: DPDP compliant only',
            'No data sale to third parties',
          ],
          purpose: 'We comply with Indian data localization requirements',
        },
      ],
      sslNotice: '🔒 SSL Secure Connection Active - All communications encrypted',
      contactTitle: 'Data Protection Officer',
      contactInfo: 'For privacy inquiries: dpo@gencarbon.in',
    },
    hi: {
      title: 'DPDP गोपनीयता और डेटा संरक्षण',
      subtitle: 'डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम 2023 अनुपालन',
      sections: [
        {
          icon: ShieldCheckIcon,
          title: 'डेटा संग्रह और उद्देश्य',
          items: [
            'उपयोगकर्ता लॉगिन क्रेडेंशियल (एन्क्रिप्टेड)',
            'सुरक्षा निगरानी के लिए आईपी पते',
            'भौगोलिक स्थान डेटा (शहर/क्षेत्र स्तर)',
            'सत्र मेटाडेटा और टाइमस्टैम्प',
            'ब्राउज़र और डिवाइस जानकारी',
          ],
          purpose: 'उद्देश्य: सुरक्षा, सेवा सुधार, नियामक अनुपालन, धोखाधड़ी रोकथाम',
        },
        {
          icon: LockClosedIcon,
          title: 'डेटा सुरक्षा और भंडारण',
          items: [
            'सभी डेटा ट्रांसमिशन के लिए SSL/TLS एन्क्रिप्शन',
            'संग्रहीत डेटा के लिए AES-256 एन्क्रिप्शन',
            'भारत में स्थित सुरक्षित सर्वर',
            'नियमित सुरक्षा ऑडिट और अनुपालन जांच',
            'डेटा प्रतिधारण: 7 वर्ष (DPDP अधिनियम के अनुसार)',
          ],
          purpose: 'आपका डेटा बैंक-ग्रेड सुरक्षा मानकों से सुरक्षित है',
        },
        {
          icon: DocumentTextIcon,
          title: 'DPDP अधिनियम के तहत आपके अधिकार',
          items: [
            'अपने व्यक्तिगत डेटा तक पहुंचने का अधिकार',
            'गलत डेटा को सुधारने का अधिकार',
            'डेटा मिटाने/हटाने का अधिकार',
            'डेटा पोर्टेबिलिटी का अधिकार',
            'सहमति वापस लेने का अधिकार',
          ],
          purpose: 'अपने अधिकारों का प्रयोग करें: dr.nupur@gencarbon.in',
        },
        {
          icon: GlobeAltIcon,
          title: 'अंतर्राष्ट्रीय डेटा स्थानांतरण',
          items: [
            'प्राथमिक डेटा भंडारण: भारत',
            'बैकअप सर्वर: ISO 27001 प्रमाणित सुविधाएं',
            'सीमा-पार स्थानांतरण: केवल अनुमोदित देशों में',
            'तृतीय-पक्ष प्रोसेसर: केवल DPDP अनुपालन',
            'तीसरे पक्ष को डेटा बिक्री नहीं',
          ],
          purpose: 'हम भारतीय डेटा स्थानीयकरण आवश्यकताओं का पालन करते हैं',
        },
      ],
      sslNotice: '🔒 SSL सुरक्षित कनेक्शन सक्रिय - सभी संचार एन्क्रिप्टेड',
      contactTitle: 'डेटा संरक्षण अधिकारी',
      contactInfo: 'गोपनीयता पूछताछ के लिए: dpo@gencarbon.in',
    },
    te: {
      title: 'DPDP గోప్యత & డేటా రక్షణ',
      subtitle: 'డిజిటల్ వ్యక్తిగత డేటా రక్షణ చట్టం 2023 అనుగుణత',
      sections: [
        {
          icon: ShieldCheckIcon,
          title: 'డేటా సేకరణ & ప్రయోజనం',
          items: [
            'వినియోగదారు లాగిన్ క్రెడెన్షియల్స్ (ఎన్క్రిప్టెడ్)',
            'భద్రతా పర్యవేక్షణ కోసం IP చిరునామాలు',
            'భౌగోళిక స్థాన డేటా (నగరం/ప్రాంత స్థాయి)',
            'సెషన్ మెటాడేటా మరియు టైమ్‌స్టాంప్‌లు',
            'బ్రౌజర్ మరియు పరికర సమాచారం',
          ],
          purpose: 'సేకరణ కారణం: భద్రత, సేవా మెరుగుదల, నియంత్రణ అనుగుణత, మోసం నివారణ',
        },
        {
          icon: LockClosedIcon,
          title: 'డేటా భద్రత & నిల్వ',
          items: [
            'అన్ని డేటా ట్రాన్స్‌మిషన్‌ల కోసం SSL/TLS ఎన్క్రిప్షన్',
            'నిల్వ చేయబడిన డేటా కోసం AES-256 ఎన్క్రిప్షన్',
            'భారతదేశంలో ఉన్న సురక్షిత సర్వర్లు',
            'క్రమం తప్పకుండా భద్రతా ఆడిట్‌లు మరియు అనుగుణత తనిఖీలు',
            'డేటా నిలుపుదల: 7 సంవత్సరాలు (DPDP చట్టం ప్రకారం)',
          ],
          purpose: 'మీ డేటా బ్యాంక్-గ్రేడ్ భద్రతా ప్రమాణాలతో రక్షించబడింది',
        },
        {
          icon: DocumentTextIcon,
          title: 'DPDP చట్టం క్రింద మీ హక్కులు',
          items: [
            'మీ వ్యక్తిగత డేటాను యాక్సెస్ చేసే హక్కు',
            'తప్పు డేటాను సరిదిద్దే హక్కు',
            'డేటాను తొలగించే/తొలగించే హక్కు',
            'డేటా పోర్టబిలిటీ హక్కు',
            'సమ్మతిని ఉపసంహరించుకునే హక్కు',
          ],
          purpose: 'మీ హక్కులను ఉపయోగించండి: dr.nupur@gencarbon.in',
        },
        {
          icon: GlobeAltIcon,
          title: 'అంతర్జాతీయ డేటా బదిలీలు',
          items: [
            'ప్రాథమిక డేటా నిల్వ: భారతదేశం',
            'బ్యాకప్ సర్వర్లు: ISO 27001 ధృవీకరించబడిన సౌకర్యాలు',
            'క్రాస్-బోర్డర్ బదిలీలు: ఆమోదించబడిన దేశాలకు మాత్రమే',
            'థర్డ్-పార్టీ ప్రాసెసర్లు: DPDP అనుగుణత మాత్రమే',
            'మూడవ పక్షాలకు డేటా విక్రయం లేదు',
          ],
          purpose: 'మేము భారతీయ డేటా స్థానికీకరణ అవసరాలను కట్టుబడి ఉన్నాము',
        },
      ],
      sslNotice: '🔒 SSL సురక్షిత కనెక్షన్ చురుకుగా ఉంది - అన్ని కమ్యూనికేషన్లు ఎన్క్రిప్ట్ చేయబడ్డాయి',
      contactTitle: 'డేటా రక్షణ అధికారి',
      contactInfo: 'గోప్యత విచారణల కోసం: dpo@gencarbon.in',
    },
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-20 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SSL Security Notice */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-lg shadow-lg mb-8 text-center">
          <p className="font-semibold text-lg">{lang.sslNotice}</p>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {lang.title}
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {lang.subtitle}
          </p>
        </div>

        {/* Privacy Sections Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {lang.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 border-2 border-emerald-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-2xl"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-gradient-to-br from-emerald-500 to-green-600 p-4 rounded-xl">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>

                <ul className="space-y-3 mb-6">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="text-emerald-600 mt-1">✓</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-500">
                  <p className="text-sm text-gray-700 font-medium">
                    {section.purpose}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">{lang.contactTitle}</h3>
          <p className="text-lg text-emerald-100">{lang.contactInfo}</p>
        </div>
      </div>
    </section>
  );
};

export default DPDPPrivacyDisclosure;